import InvestmentModel from '@advisor/models/store/investmentModel/index';
import ModelPortolio from '@advisor/models/store/investmentModel/ModelPortolio';
import Position from '@advisor/models/store/investmentModel/Position';
import {TAX_SETTINGS, NOTIFICATION_TYPE} from '@advisor/util/constants';
import exceptionHandler from '@advisor/util/exceptionHandler';
import RwHoldingsService from '@advisor/rwSdk/HoldingsService';
import {addRiaComponentTo} from '@advisor/util/store/paramInjects';
import _ from 'lodash';
import {HTTP_STATUS} from '@advisor/util/HttpStatus';

export const stateObj = () => new InvestmentModel();
const sortByWeight = (tickA, tickB) => tickB.tickerWeight - tickA.tickerWeight;

export const getters = {
  modelPortfolioList: (state) => {
    let arrModelPortfolios = [];

    if (Array.isArray(state.modelPortfolios)) {
      arrModelPortfolios = InvestmentModel.fetchModelPortfolios(state.modelPortfolios);
    }

    return Object.freeze(arrModelPortfolios);
  },

  combinedModelAndFrameworks: (state) => {
    let combinedResult = [];

    if (Array.isArray(state.modelPortfolios)) {
      combinedResult = [...InvestmentModel.fetchModelPortfolios(state.modelPortfolios), ...state.glidePathFrameworks];
    }

    combinedResult = combinedResult.map((investmentModel) => {
      const {modelId, modelName, typeId} = investmentModel;

      return {modelId, modelName, typeId};
    });

    return Object.freeze(combinedResult);
  },

  availableTickersMap: (state) =>
    state.selects.tickersAll.reduce((availableTickers, tick) => {
      availableTickers.set(tick.composite ? `cp-${tick.compositeName}` : `tk-${tick.tickerName}-${tick.ticker}`, tick);

      return availableTickers;
    }, new Map()),

  sleeveTickerMap: (state) => {
    const sleeveTicker = new Map();

    state.sleevesTickers.forEach((tick) => {
      sleeveTicker.set(tick.tickerId, tick);
    });

    return sleeveTicker;
  },
};

export const actions = {
  // eslint-disable-next-line consistent-return
  async getAssetManagers({commit, rootState}) {
    try {
      const {riaId} = rootState;
      const fullPayload = addRiaComponentTo({riaId}, rootState.settings.default);
      const sdkResponse = await RwHoldingsService.getAssetManagers(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_ASSET_MANAGERS', sdkResponse);

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentModel.getAssetManagers', error);
    }
  },
  // eslint-disable-next-line consistent-return
  async getReferences({rootState}, {modelId, type}) {
    try {
      const {riaId} = rootState;
      const payload = {id: Number(modelId), type, riaId};
      const sdkResponse = await RwHoldingsService.getReferences(payload).catch((error) => {
        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  /**
   * This will retrieve all the model portfolios
   *
   * @for ModelPortfolio
   * @param {Object} param0
   * @param {Object} payload
   */
  // eslint-disable-next-line consistent-return
  async getModelPortfolios({commit, dispatch, rootState}) {
    try {
      const {riaId} = rootState;
      const fullPayload = addRiaComponentTo({riaId}, rootState.settings.default);
      const sdkResponse = await RwHoldingsService.getModelPortfolios(fullPayload).catch((error) => {
        throw error;
      });

      await dispatch('filterAssetManagers', sdkResponse);
      commit('SET_MODEL_PORTFOLIOS', sdkResponse);

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentModel.getModelPortfolios', error);
    }
  },
  /**
   * This will retrieve selected model portfolio and set it to store.
   *
   * @for ModelPortfolio
   * @param {Object} param0
   * @param {Object} payload
   */
  // eslint-disable-next-line consistent-return
  async getModelPortfolioData({commit, rootState}, payload) {
    try {
      const COMPOSITION_WEIGHT_CALCULATED = true;
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);
      const sdkResponse = await RwHoldingsService.getModelPortfolioData(fullPayload).catch((error) => {
        throw error;
      });
      /**
       * We are getting calculated compositions weight so we need to find actual weight of compositions for further calculations
       */
      const objModelPortolio = ModelPortolio.make(sdkResponse, COMPOSITION_WEIGHT_CALCULATED);

      commit('SET_MODEL_PORTFOLIO_DATA', objModelPortolio);

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentModel.getModelPortfolioData', error);
    }
  },
  /**
   * This will return compositions of Sleeve
   * @param {Object} param0
   * @param {Object} payload
   */
  // eslint-disable-next-line consistent-return
  async getModelPortfolioComposites({commit, rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);
      const sdkResponse = await RwHoldingsService.getSleeveById(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_MODEL_PORTFOLIO_COMPOSITES', sdkResponse);

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentModel.getModelPortfolioComposites', error);
    }
  },
  /**
   * This will return tickers of particular model portfolio
   * @for ModelPortfolio
   * @param {Object} param0
   * @param {Object} payload
   */
  // eslint-disable-next-line consistent-return
  async getPortfolioTickers({commit, rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);
      const sdkResponse = await RwHoldingsService.getPortfolioTickers(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_PORTFOLIO_TICKERS', sdkResponse);

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentModel.getPortfolioTickers', error);
    }
  },
  // eslint-disable-next-line consistent-return
  loadGlidePathFrameworkDetails({commit}, payload) {
    commit('LOAD_GLIDEPATH_FRAMEWORK_DETAILS', payload);
  },
  /**
   * This function is used to create or update the model portfolio
   * @for ModelPortfolio
   * @param {Object} param0
   * @param {Object} payload
   */
  // eslint-disable-next-line consistent-return
  async createUpdateModelPortfolio({rootState}, payload) {
    try {
      const {riaId} = rootState;
      const modelPortfolio = payload;

      modelPortfolio.modelPortfolioDetails = modelPortfolio.modelPortfolioDetails.map((ticker) => {
        const tickerItem = ticker;

        tickerItem.tickerTax = tickerItem.tickerTax === 4 ? 1 : tickerItem.tickerTax;

        return tickerItem;
      });

      let sdkResponse = null;
      const fullPayload = addRiaComponentTo({...modelPortfolio, riaId}, rootState.settings.default);

      sdkResponse = await (modelPortfolio.modelId && modelPortfolio.modelId !== 'new'
        ? RwHoldingsService.updateModelPortfolioData(fullPayload).catch((error) => {
            throw error;
          })
        : RwHoldingsService.createModelPortfolioData(fullPayload).catch((error) => {
            throw error;
          }));

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentModel.createUpdateModelPortfolio', error);

      return Promise.reject(error);
    }
  },
  /**
   * This function is used to delete the model portfolio
   * @for ModelPortfolio
   * @param {Object} param0
   * @param {modelId} payload
   */
  // eslint-disable-next-line consistent-return
  async deleteModelPortfolio({rootState}, modelId) {
    try {
      const {riaId} = rootState;
      const fullPayload = addRiaComponentTo({modelId, riaId}, rootState.settings.default);
      const sdkResponse = await RwHoldingsService.deleteModelPortfolio(fullPayload).catch((error) => {
        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentModel.deleteModelPortfolio', error);
    }
  },
  /**
   * To clear the data of model portfolio from stroe
   * @for ModelPortfolio
   * @param {Object} param0
   * @param {Object} payload
   */
  // eslint-disable-next-line consistent-return
  setModelPortfolioData({commit}, payload) {
    if (!payload) {
      const newModelPortfolio = new InvestmentModel().modelPortfolioData;
      const objPosition = Position.make({}, true);

      newModelPortfolio.addNewPosition(objPosition);
      commit('SET_MODEL_PORTFOLIO_DATA', newModelPortfolio);

      return;
    }

    commit('SET_MODEL_PORTFOLIO_DATA', payload);
  },
  // eslint-disable-next-line consistent-return
  filterAssetManagers({commit, state}, payload) {
    try {
      const managersList = state.assetManagers;
      const filterdList = [{id: 0, name: 'All Managers'}];
      const arr = _.uniqBy(payload, 'assetManagerId');

      arr.forEach((item) => {
        managersList.forEach((element) => {
          if (element.id === item.assetManagerId) {
            filterdList.push(element);
          }
        });
      });

      commit('SET_FILTERED_ASSET_MANAGERS', filterdList);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentModel.filterAssetManagers', error);
    }
  },
  // eslint-disable-next-line consistent-return
  async getGlidePathFrameworks({commit, dispatch, rootState}) {
    try {
      const {riaId} = rootState;
      const fullPayload = addRiaComponentTo({riaId}, rootState.settings.default);
      const sdkResponse = await RwHoldingsService.getGlidePathFrameworks(fullPayload).catch((error) => {
        throw error;
      });

      await dispatch('filterAssetManagers', sdkResponse);
      commit('SET_GLIDE_PATH_FRAMEWORK', sdkResponse);

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentModel.getGlidePathFrameworks', error);
    }
  },
  // eslint-disable-next-line consistent-return
  async getGlidePathFrameworkTypes({commit, rootState}) {
    try {
      const {riaId} = rootState;
      const fullPayload = addRiaComponentTo({riaId}, rootState.settings.default);
      const sdkResponse = await RwHoldingsService.getGlidePathFrameworkTypes(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_GLIDE_PATH_FRAMEWORK_TYPES', sdkResponse);

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentModel.getGlidePathFrameworkTypes', error);
    }
  },
  // eslint-disable-next-line consistent-return
  addTempGlidepPathFramework({commit}, payload) {
    commit('SET_NEW_GLIDE_PATH_FRAMEWORK', payload);
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, consistent-return
  async createOrUpdateGlidePathFramework({rootState}, payload) {
    try {
      const {riaId} = rootState;
      const fullPayload = addRiaComponentTo({...payload, frameworkId: payload?.modelId, riaId}, rootState.settings.default);

      const response = await (payload.modelId && payload.modelId !== ''
        ? RwHoldingsService.updateGlidePathFrameworks(fullPayload).catch((error) => {
            throw error;
          })
        : RwHoldingsService.createGlidePathFrameworks(fullPayload).catch((error) => {
            throw error;
          }));

      return Promise.resolve(response);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  // eslint-disable-next-line consistent-return
  async deleteGlidePathFramework({rootState}, {modelId}) {
    try {
      const {riaId} = rootState;
      const fullPayload = addRiaComponentTo({riaId, frameworkId: modelId}, rootState.settings.default);
      const sdkResponse = await RwHoldingsService.deleteGlidePathFramework(fullPayload).catch((error) => {
        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      return Promise.reject(error);
    }
  },
  // eslint-disable-next-line consistent-return
  updateGlidePathDetails({commit}, _payload) {
    let payload = _payload;

    if (!_payload) {
      payload = InvestmentModel.getNewGlidePathFramework();
    }

    commit('UPDATE_GLIDEPATH_DETAILS', payload);
  },
  // eslint-disable-next-line consistent-return
  async fetchSleeves({commit, dispatch, rootState}) {
    try {
      const {riaId} = rootState;
      const fullPayload = addRiaComponentTo({riaId}, rootState.settings.default);
      const sdkResponse = await RwHoldingsService.getSleeves(fullPayload).catch((error) => {
        throw error;
      });

      await dispatch('filterAssetManagers', sdkResponse);
      commit('SET_SLEEVES_LIST', sdkResponse);

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentModel.fetchSleeves', error);
    }
  },
  // eslint-disable-next-line consistent-return
  async getSleeveDetails({commit, rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);
      const sdkResponse = await RwHoldingsService.getSleeveById(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_SLEEVE_DETAILS', sdkResponse);

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentModel.getSleeveDetails', error);
    }
  },
  // eslint-disable-next-line consistent-return
  async getSleeveTickers({commit, rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);
      const sdkResponse = await RwHoldingsService.getSleeveTickers(fullPayload).catch((error) => {
        throw error;
      });

      commit('SET_SLEEVE_TICKERS', sdkResponse);

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentModel.getSleeveTickers', error);
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, consistent-return
  async addNewSleeve({rootState, dispatch}, request) {
    const msg = {
      type: NOTIFICATION_TYPE.ERROR,
      text: 'Sleeve name already taken. Try a different name.',
      timeout: 5,
    };

    try {
      const {payload, sleeveId} = request;
      const fullPayload = addRiaComponentTo({...payload, sleeveId}, rootState.settings.default);
      const sdkResponse = await RwHoldingsService.createSleeve(fullPayload).catch((error) => {
        this.dispatch('notification/setMessage', msg);

        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      if (error.response && error.response.status === HTTP_STATUS.CONFLICT.getStatusCode()) {
        dispatch('notification/setMessage', msg);
      }

      exceptionHandler.handleGenericException('investmentModel.addNewSleeve', error);
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, consistent-return
  async updateSleeve({rootState, dispatch}, request) {
    const msg = {
      type: NOTIFICATION_TYPE.ERROR,
      text: 'Sleeve name already taken. Try a different name.',
      timeout: 5,
    };

    try {
      const {payload, sleeveId} = request;
      const fullPayload = addRiaComponentTo({...payload, sleeveId}, rootState.settings.default);
      const sdkResponse = await RwHoldingsService.updateSleeve(fullPayload).catch((error) => {
        dispatch('notification/setMessage', msg);

        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      if (error.response && error.response.status === HTTP_STATUS.CONFLICT.getStatusCode()) {
        dispatch('notification/setMessage', msg);
      }

      exceptionHandler.handleGenericException('investmentModel.updateSleeve', error);
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, consistent-return
  async checkSleeveOccupation({rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo({...payload, id: payload.sleeveId}, rootState.settings.default);
      const sdkResponse = await RwHoldingsService.getReferences(fullPayload).catch((error) => {
        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentModel.checkSleeveOccupation', error);
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, consistent-return
  async deleteSleeve({rootState}, payload) {
    try {
      const fullPayload = addRiaComponentTo(payload, rootState.settings.default);
      const sdkResponse = await RwHoldingsService.deleteSleeve(fullPayload).catch((error) => {
        throw error;
      });

      return Promise.resolve(sdkResponse);
    } catch (error) {
      exceptionHandler.handleGenericException('investmentModel.deleteSleeve', error);
    }
  },
};

export const mutations = {
  SET_MODEL_PORTFOLIOS(state, payload) {
    state.modelPortfolios = payload;
  },
  SET_GLIDE_PATH_FRAMEWORK(state, payload) {
    state.glidePathFrameworks = payload;
  },
  SET_MODEL_PORTFOLIO_DATA(state, payload) {
    payload.modelPortfolioDetails.sort(sortByWeight);
    state.modelPortfolioData = payload;
    state.defaultModelPortfolioData = _.cloneDeep(payload);
  },
  SET_MODEL_PORTFOLIO_COMPOSITES(state, payload) {
    state.modelPortfolioComposites = payload;
  },
  SET_NEW_GLIDE_PATH_FRAMEWORK(state, payload) {
    state.newGlidePathFramework.gpId = payload.glidePathType;
    state.newGlidePathFramework.modelName = payload.name;
    state.newGlidePathFramework.description = payload.description;
  },
  SET_PORTFOLIO_TICKERS(state, payload) {
    const tickersOf = (tickersArr, type) =>
      tickersArr
        .filter((ticker) => ticker.tickerType === type && ticker.tickTax !== null)

        .map((ticker) => {
          const retVal = ticker;

          retVal.id = ticker.tickerId;
          retVal.tickerId = type === 'cp' ? 0 : retVal.id;
          retVal.composite = type === 'cp';
          retVal.compositeId = type === 'cp' ? retVal.id : 0;
          retVal.compositeName = type === 'cp' ? retVal.tickerName : '';
          // Workaround for ticker values from the API endpoint. This needs to be checked
          retVal.tickerTax += retVal.tickerTax === 0 ? 1 : 0;
          retVal.taxTitle = TAX_SETTINGS.NAMES[retVal.tickerTax - 1];
          retVal.tickerType = type === 'cp' ? 'Mixed' : retVal.tickerType;

          return retVal;
        });

    state.selects.composites = tickersOf(payload, 'cp');
    state.selects.tickers = tickersOf(payload, 'tk');
    state.selects.tickersAll = [...state.selects.composites, ...state.selects.tickers];
  },
  SET_SLEEVE_TICKERS(state, payload) {
    state.sleevesTickers = payload;
  },
  SET_SLEEVES_LIST(state, payload) {
    state.sleevesList = payload;
  },
  SET_SLEEVE_DETAILS(state, payload) {
    state.sleeveDetails = payload;
    state.sleeveDetails.lines.sort(sortByWeight);
  },
  SET_GLIDE_PATH_FRAMEWORK_TYPES(state, payload) {
    state.glidePathFrameworkTypes = payload;
  },
  SET_ASSET_MANAGERS(state, payload) {
    state.assetManagers = payload;
  },
  SET_FILTERED_ASSET_MANAGERS(state, payload) {
    state.filteredManagers = payload;
  },
  LOAD_GLIDEPATH_FRAMEWORK_DETAILS(state, payload) {
    state.glidePathFrameworkDetails = payload;
  },
  UPDATE_GLIDEPATH_DETAILS(state, payload) {
    state.glidePathFrameworkDetails = {
      ...state.glidePathFrameworkDetails,
      ...payload,
    };
  },
};

// eslint-disable-next-line import/no-named-as-default-member
export default {
  namespaced: true,
  state: stateObj,
  getters,
  actions,
  mutations,
};
